/**
 * Follow-ups — the pure rules behind the page chrome (redesign Phase 1B, 2026-10-02).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 *   FOLLOWUP_TABS / visibleTabs   the tab bar across the four follow-up pages
 *   addDays / addMonths           calendar arithmetic on 'YYYY-MM-DD' (no time zone involved)
 *   dateRangePresets              "Last 3 / 6 / 12 / 24 months"
 *   dateRangeError                the date popover's validation message (old page: the 24-month toast)
 *   placePopover                  where a filter popover goes so it stays inside the viewport
 *   goalProgress                  a call-goal card's numbers
 *   sortListsNewestFirst          the "Your lists" menu order
 */

// ---- Tabs ------------------------------------------------------------------------------------

/**
 * The four follow-up pages. `route` is the EXISTING route (src/Routes.js); `subject` is the CASL
 * subject the app's header link checks for that page — Header.vue shows a link when
 * `$can('view', subject) || $can('view', 'all')`, and a tab is shown by the same rule. (Aging's
 * route accepts any of three subjects, but its header link checks outstanding_claim only; the tab
 * follows the header so the two never disagree.)
 */
export const FOLLOWUP_TABS = [
  { key: 'overdueRecall', label: 'Overdue recall', route: { name: 'OverdueRecall' }, subject: 'overdue_recall' },
  { key: 'unscheduledTreatment', label: 'Unscheduled treatment', route: { name: 'unscheduledTreatment' }, subject: 'unscheduled_treatment' },
  { key: 'aging', label: 'Aging', route: { name: 'Aging' }, subject: 'outstanding_claim' },
  { key: 'brokenAppointments', label: 'Broken appointments', route: { name: 'BrokenAppointments' }, subject: 'broken_appointment' },
];

/**
 * The tabs this user may see, each with its due-now count — a number only where the caller has
 * the page's rows loaded (`counts[key]`); anything else is null and draws no badge.
 * @param {Function} can     (action, subject) → boolean — the app's $can
 * @param {Object}   counts  { [tabKey]: number }
 */
export function visibleTabs(can, counts) {
  const c = counts || {};
  const allowed = typeof can === 'function'
    ? t => !!(can('view', t.subject) || can('view', 'all'))
    : () => false;
  return FOLLOWUP_TABS.filter(allowed).map(t => ({
    key: t.key,
    label: t.label,
    to: t.route,
    count: typeof c[t.key] === 'number' && Number.isFinite(c[t.key]) ? c[t.key] : null,
  }));
}

// ---- Dates -----------------------------------------------------------------------------------

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const pad = v => (v < 10 ? `0${v}` : String(v));

function fromParts(y, m, d) {
  const dt = new Date(Date.UTC(y, m, d));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** A real calendar date in 'YYYY-MM-DD' (2026-02-30 is not). */
export function isYmd(value) {
  if (typeof value !== 'string' || !YMD.test(value)) return false;
  return fromParts(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10))) === value;
}

export function addDays(ymd, n) {
  return fromParts(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)) + n);
}

/** moment's add(n, 'months'): the same day of the month, clamped to the month's last day. */
export function addMonths(ymd, n) {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7)) - 1 + n;
  const d = Number(ymd.slice(8, 10));
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return fromParts(y, m, Math.min(d, last));
}

/**
 * "Last N months" presets. `from` is today minus N months — the same rule the old page uses for
 * its default range (moment().subtract(6, 'months')), so "Last 6 months" is lit on a fresh load.
 */
export function dateRangePresets(today, months) {
  return (months || [3, 6, 12, 24]).map(m => ({
    label: `Last ${m} month${m === 1 ? '' : 's'}`,
    from: addMonths(today, -m),
    to: today,
  }));
}

/**
 * The date popover's message, or '' when the range can be applied.
 * `maxMonths` mirrors the old page's applyFilter(): moment(to).diff(from, 'months', true) > 24 is
 * refused — i.e. `to` later than `from` + 24 months.
 */
export function dateRangeError(from, to, { maxMonths = 24 } = {}) {
  if (!from || !to) return 'Pick both dates.';
  if (!isYmd(from) || !isYmd(to)) return 'Enter a valid date.';
  if (from > to) return 'The start date needs to come before the end date.';
  if (maxMonths && to > addMonths(from, maxMonths)) return `Pick a range of ${maxMonths} months or less.`;
  return '';
}

// ---- Popover placement -----------------------------------------------------------------------

/**
 * Where a filter popover sits. Desktop / tablet: fixed under its button (above it when there is
 * not room below and more room above), `align` 'left' lines its left edge up with the button's,
 * 'right' its right edge, then clamped `edge` px inside the viewport either side. Phone (< 640px
 * wide): a bottom sheet, `edge`-less 8px insets — the export's place().
 *
 * @param {Object} anchor    the button's DOMRect (top, bottom, left, right)
 * @param {Object} size      { width, height } — height is an ESTIMATE used only to pick a side
 * @param {Object} viewport  { width, height }
 * @returns {{ sheet: boolean, top?, bottom?, left?, width?, maxHeight? }} numbers in px
 */
export function placePopover(anchor, { width = 320, height = 320, align = 'left' } = {}, viewport = {}, { edge = 12, gap = 6, phoneMax = 640 } = {}) {
  const vw = viewport.width || 1280;
  const vh = viewport.height || 800;
  if (vw < phoneMax) return { sheet: true };
  const a = anchor || { top: edge, bottom: edge, left: edge, right: edge + width };
  const w = Math.min(width, vw - edge * 2);
  const wanted = align === 'right' ? a.right - w : a.left;
  const left = Math.max(edge, Math.min(wanted, vw - w - edge));
  const below = vh - a.bottom;
  if (below < height + 16 && a.top > below) {
    return {
      sheet: false,
      left,
      width: w,
      bottom: Math.max(edge, vh - a.top + gap),
      maxHeight: Math.max(160, a.top - gap - edge),
    };
  }
  const top = Math.max(edge, a.bottom + gap);
  return { sheet: false, left, width: w, top, maxHeight: Math.max(160, vh - top - edge) };
}

// ---- Goals -----------------------------------------------------------------------------------

/**
 * A call-goal card. `done` is the page's attempts count; `goal` is the setting's PropertyValue
 * (a string, or missing). The bar is capped at 100%; the numbers are never rounded or capped —
 * the card shows exactly what the old page shows ("12 / 20").
 */
export function goalProgress(done, goal) {
  const n = Number(done) || 0;
  const hasGoal = goal !== undefined && goal !== null && String(goal).trim() !== '' && Number.isFinite(Number(goal));
  const g = hasGoal ? Number(goal) : null;
  const pct = g && g > 0 ? Math.max(0, Math.min(100, (n / g) * 100)) : 0;
  return {
    done: n,
    goal: hasGoal ? String(goal).trim() : '',
    pct,
    reached: g !== null && g > 0 && n >= g,
    remaining: g !== null && g > n ? g - n : 0,
  };
}

// ---- Lists -----------------------------------------------------------------------------------

/** Newest first by `field` (CreatedAt); undated lists last; ties keep their order. Never mutates. */
export function sortListsNewestFirst(lists, field = 'CreatedAt') {
  const time = (l) => {
    const t = l && l[field] ? Date.parse(String(l[field]).replace(' ', 'T')) : NaN;
    return Number.isFinite(t) ? t : -Infinity;
  };
  return (lists || [])
    .map((l, i) => ({ l, i, t: time(l) }))
    .sort((a, b) => (b.t - a.t) || (a.i - b.i))
    .map(x => x.l);
}
